/**
 * Aligning a chapter's visible text as a note was anchored in it (the base: a reading link's pinned snapshot) with the
 * text now (decision 58: "the writer's device diffs the shared snapshot against the current chapter to map the range";
 * sync case table C7; part 39 log, AC1, AC6). The runs both texts hold unchanged are found line by line (a line's end
 * apart, so a paragraph moved to the end is the same), as patience diff finds them: the lines both start and end with,
 * then the lines found once in each, in the order both keep them, and so on between those; inside a stretch of changed
 * lines, the characters it starts and ends with in both. A place in the base
 * maps through the run it is in; a place in a changed stretch maps to that stretch's start or end in the text now. The
 * caller checks what it finds there (AC1): the alignment is a guide, never the proof. Pure.
 */

/** Which end of a range a place is: a changed stretch maps a start to its start, an end to its end. */
export type Side = 'start' | 'end';

/** A run both texts hold unchanged: `length` units from `base` in the base and from `now` in the text now. */
interface Run {
  readonly base: number;
  readonly now: number;
  readonly length: number;
}

/** A text's lines, without their line ends (a paragraph is the same line last or not), and where each starts. */
interface Lines {
  readonly texts: readonly string[];
  readonly starts: readonly number[];
  /** The text's length: where a line past the last would start. */
  readonly length: number;
}

/** `text`'s lines. */
function linesOf(text: string): Lines {
  const texts = text.split('\n');
  const starts: number[] = [];
  let at = 0;
  for (const line of texts) {
    starts.push(at);
    at += line.length + 1;
  }
  return { texts, starts, length: text.length };
}

/** Where line `index` of `lines` starts, or the text's end past the last. */
function startOf(lines: Lines, index: number): number {
  return lines.starts[index] ?? lines.length;
}

/** The longest increasing run of `values` (each distinct), as indices into it: patience sorting, n log n. */
export function longestIncreasing(values: readonly number[]): number[] {
  // tails[k]: the index of the smallest last value of an increasing run of length k + 1.
  const tails: number[] = [];
  const previous = new Array<number>(values.length).fill(-1);
  values.forEach((value, index) => {
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (Number(values[Number(tails[middle])]) < value) low = middle + 1;
      else high = middle;
    }
    if (low > 0) previous[index] = Number(tails[low - 1]);
    tails[low] = index;
  });
  const run: number[] = [];
  for (let index = tails.at(-1) ?? -1; index >= 0; index = Number(previous[index])) run.push(index);
  return run.reverse();
}

/** How often each line's text is in `lines[from, to)`. */
function counts(lines: Lines, from: number, to: number): Map<string, number> {
  const seen = new Map<string, number>();
  for (let index = from; index < to; index += 1) {
    const text = String(lines.texts[index]);
    seen.set(text, (seen.get(text) ?? 0) + 1);
  }
  return seen;
}

/** The base and the text now aligned: {@link alignTexts}. */
export class Alignment {
  /** The runs in the base's order (also the text now's): where each starts in the base and now, and its length. */
  readonly #bases: readonly number[];
  readonly #nows: readonly number[];
  readonly #lengths: readonly number[];
  readonly #nowLength: number;

  constructor(runs: readonly Run[], nowLength: number) {
    this.#bases = runs.map((run) => run.base);
    this.#nows = runs.map((run) => run.now);
    this.#lengths = runs.map((run) => run.length);
    this.#nowLength = nowLength;
  }

  /**
   * AC1: where the base's offset `at` is in the text now: through the run holding it, or, in a changed stretch, that
   * stretch's start (`side` start) or end (`side` end) in the text now.
   */
  map(at: number, side: Side): number {
    // The runs starting at or before `at`: the last of them is `low - 1`.
    let low = 0;
    let high = this.#bases.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (Number(this.#bases[middle]) <= at) low = middle + 1;
      else high = middle;
    }
    if (low > 0) {
      const base = Number(this.#bases[low - 1]);
      const now = Number(this.#nows[low - 1]);
      const length = Number(this.#lengths[low - 1]);
      if (at <= base + length) return now + (at - base);
      if (side === 'start') return now + length;
    } else if (side === 'start') return 0;
    return this.#nows[low] ?? this.#nowLength;
  }
}

/** AC1, AC6: `base` aligned with `now`. */
export function alignTexts(base: string, now: string): Alignment {
  const a = linesOf(base);
  const b = linesOf(now);
  const same = (i: number, j: number): boolean => a.texts[i] === b.texts[j];
  const runs: Run[] = [];
  const line = (i: number, j: number): void => {
    runs.push({ base: startOf(a, i), now: startOf(b, j), length: String(a.texts[i]).length });
  };
  // Stretches of lines still to align: [a0, a1) of the base against [b0, b1) of the text now.
  const work: [number, number, number, number][] = [[0, a.texts.length, 0, b.texts.length]];
  for (let next = work.pop(); next !== undefined; next = work.pop()) {
    let [a0, a1, b0, b1] = next;
    while (a0 < a1 && b0 < b1 && same(a0, b0)) line(a0++, b0++);
    while (a0 < a1 && b0 < b1 && same(a1 - 1, b1 - 1)) line(--a1, --b1);
    if (a0 === a1 || b0 === b1) continue;
    // The lines found once on each side, in the base's order, and the longest run of them in the same order now.
    const inA = counts(a, a0, a1);
    const inB = counts(b, b0, b1);
    const where = new Map<string, number>();
    for (let j = b0; j < b1; j += 1) {
      const text = String(b.texts[j]);
      if (inB.get(text) === 1 && inA.get(text) === 1) where.set(text, j);
    }
    const pairs: (readonly [number, number])[] = [];
    for (let i = a0; i < a1; i += 1) {
      const j = where.get(String(a.texts[i]));
      if (j !== undefined) pairs.push([i, j]);
    }
    const kept = new Set(longestIncreasing(pairs.map(([, j]) => j)));
    const anchors = pairs.filter((_, index) => kept.has(index));
    if (anchors.length === 0) {
      chars(base.slice(startOf(a, a0), startOf(a, a1)), now.slice(startOf(b, b0), startOf(b, b1)), startOf(a, a0), startOf(b, b0), runs);
      continue;
    }
    let [i0, j0] = [a0, b0];
    for (const [i, j] of anchors) {
      work.push([i0, i, j0, j]);
      line(i, j);
      [i0, j0] = [i + 1, j + 1];
    }
    work.push([i0, a1, j0, b1]);
  }
  runs.sort((x, y) => x.base - y.base);
  return new Alignment(
    runs.filter((run) => run.length > 0),
    now.length,
  );
}

/** A changed stretch, `x` at `atX` in the base and `y` at `atY` now: the characters both start and end with, as runs. */
function chars(x: string, y: string, atX: number, atY: number, runs: Run[]): void {
  const most = Math.min(x.length, y.length);
  let head = 0;
  while (head < most && x[head] === y[head]) head += 1;
  let tail = 0;
  while (tail < most - head && x[x.length - 1 - tail] === y[y.length - 1 - tail]) tail += 1;
  runs.push({ base: atX, now: atY, length: head }, { base: atX + x.length - tail, now: atY + y.length - tail, length: tail });
}
